<template>
	<div class="width-100 height-100 flex-centerize flex-column">
		<ClipLoader v-if="isLoading" color="#5F8FF1" size="18px" />
		<div v-else-if="user" class="content-container display-flex flex-column">
			<div class="display-flex flex-column px-8 pb-6">
				<div class="width-100 height-100 flex-centerize" style="margin-top: 45%" v-if="!canInstall">
					You do not have access to install, to install request access from your workspace admin.
				</div>
				<div
					style="margin-top: 45%"
					v-else-if="didUserInstallBefore && showInstallAgainQuestion"
					class="width-100 height-100 flex-centerize flex-column text-center"
				>
					{{ installAgainText }}
					<AnemicButton @click="reFillConfiguration" class="mr-4 mt-4 no-button">Edit your feature</AnemicButton>
					<div @click="createInstallable" class="install-again-text mt-2">Install another</div>
				</div>
				<div class="width-100 height-100 flex-centerize" style="margin-top: 45%" v-else-if="installationGoneWrong">
					Something went wrong, please try again
				</div>
				<div v-else>
					<InstallablePreview class="mt-4" :installableId="installableId" />
					<div class="flex-centerize flex-column loading-container" v-if="isInstalling">
						<ClipLoader class="mb-1" color="#5F8FF1" size="24px" />
						<div class="installing-text">Installing...</div>
					</div>
					<div v-else class="display-flex flex-column flex-centerize">
						<div class="installation-content-container">
							<div class="security-info-card flex-centerize flex-row mt-5 mb-3">
								Complete the setup below to activate your feature, Your data is
								<div @click="openPrivacyAndSecurityDialog" class="ml-1 link">private and secure</div>
							</div>
							<div class="setup-menu-container">
								<SetupMenu
									@close="close"
									installationMode
									v-if="configurationIds"
									applyConfigurationsResponseOnDraft
									:configurationIds="configurationIds"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
const isMobile = require('is-mobile');
import { mapGetters, mapActions } from 'vuex';
import { mapGettersShorthand } from '@/store/utils/mappers';
import ClipLoader from 'vue-spinner/src/ClipLoader.vue';
import { AccountPermissions } from '@ply-io/ply-front-shared-utilities';
import { hasAccountPermission } from '@/store/modules/account/getters';
import { getInstallable } from '@/store/modules/installables/getters';
import { getInstallable as getInstallableAction } from '@/store/modules/installables/actions';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import api from '@/api';
import SetupMenu from '@/views/Flow/configurations/SetupMenu';
import { isInstallationRegistrationMode } from '@/utils/helpers';
import InstallablePreview from '@/components/Project/InstallablePreview.vue';
import { getConfiguration as useConfiguration } from '@/store/modules/workflowConfigurations/getters';
import { getConfiguration } from '@/store/modules/workflowConfigurations/actions.js';
import { getInstallableVersion } from '@/store/modules/installables/modules/versions/getters';
import { installInstallableVersion } from '@/store/modules/installables/modules/versions/actions';
import AnalyticsService from '@/utils/AnalyticsService';
import SignUpService from '@/utils/sign-up-service';
export default {
	name: 'InstallationContent',
	props: {
		installableId: { type: String },
		forceInstall: { type: Boolean },
		avoidRedirection: { type: Boolean },
	},
	components: {
		ClipLoader,
		SetupMenu,
		InstallablePreview,
	},
	computed: {
		...mapGetters(['user']),
		...mapGettersShorthand({ getInstallable, useConfiguration, hasAccountPermission, getInstallableVersion }),
		installable() {
			return this.getInstallable(this.installableId) || {};
		},
		installableVersionId() {
			return this.installable.current_version;
		},
		isMobile() {
			return isMobile();
		},
		isInstallationMode() {
			return isInstallationRegistrationMode();
		},
		userName() {
			return this.user.first_name + ' ' + this.user.last_name;
		},
		installableVersion() {
			return this.getInstallableVersion(this.installableId, this.installableVersionId);
		},
		configurationIds() {
			if (this.installedWorkflow) {
				return {
					generalConfigurationId: this.installedWorkflow.current_version_general_setup,
					userSpecificConfigurationId: this.installedWorkflow.current_version_user_setup,
				};
			} else {
				return null;
			}
		},
		didUserInstallBefore() {
			return this.allInstalledWorkflowsForThisInstallableVersion.length > 0;
		},
		installAgainText() {
			return 'Looks like you’ve already installed this feature';
		},
		generalConfig() {
			return this.useConfiguration(this.installedWorkflow.current_version_general_setup);
		},
		userConfig() {
			return this.useConfiguration(this.installedWorkflow.current_version_user_setup);
		},
		canInstall() {
			return this.hasAccountPermission(AccountPermissions.InstallInstallables);
		},
	},
	methods: {
		...mapActions([getInstallableAction.fullPath, 'initUser']),
		...mapActions([installInstallableVersion.fullPath, getConfiguration.fullPath]),
		close() {
			if (this.avoidRedirection) {
				this.$emit('installation-complete', this.installedWorkflow);
			} else {
				this.$router.push({ name: 'InstallationComplete', query: { wfId: this.installedWorkflow.id } });
			}
		},
		async getConfiguration(configurationId) {
			await this[getConfiguration.fullPath]({ id: configurationId, forceGet: true });
		},
		openPrivacyAndSecurityDialog() {
			AppDialogManagerService.openPrivacyAndSecurityDialog();
		},
		areConfigurationsEmpty() {
			return this.isConfigurationEmpty(this.generalConfig) && this.isConfigurationEmpty(this.userConfig);
		},
		isConfigurationEmpty(configuration) {
			return !configuration?.workflow_configuration_interfaces[0]?.interface?.fields?.length > 0;
		},
		async createInstallable() {
			this.isInstalling = true;
			this.installationGoneWrong = false;
			this.showInstallAgainQuestion = false;
			try {
				this.installedWorkflow = await this[installInstallableVersion.fullPath]({
					fields: {},
					installableId: this.installableId,
					installableVersionId: this.installableVersionId,
				});

				// If no configuration for both, call close
				if (!this.installedWorkflow.current_version_general_setup && !this.installedWorkflow.current_version_user_setup) {
					this.close();
					return;
				}

				const configurationIdsToGet = [
					this.installedWorkflow.current_version_general_setup,
					this.installedWorkflow.current_version_user_setup,
				];
				await Promise.all(
					configurationIdsToGet.map(async (configurationId) => {
						if (configurationId) {
							await this.getConfiguration(configurationId);
						}
					})
				);
				if (this.areConfigurationsEmpty()) {
					this.close();
					return;
				}
			} catch {
				this.installationGoneWrong = true;
			} finally {
				this.isInstalling = false;
				if (this.isMobile) {
					this.$router.push({
						name: 'MobileInstallation',
						query: { installableId: this.installableId },
						params: { installableId: this.installableId },
					});
				}
			}
		},
		async initialize() {
			try {
				this.allInstalledWorkflowsForThisInstallableVersion =
					(await api.getInstalledWorkflows(this.installableId))?.installed_workflows || [];
				if (this.allInstalledWorkflowsForThisInstallableVersion.length > 0) {
					this.showInstallAgainQuestion = true;
				}
			} catch {
				console.log('Couldnt fetch previous installations');
			}
		},
		reFillConfiguration() {
			this.$router.push({
				name: 'editWorkflowConfiguration',
				params: { workflowId: this.allInstalledWorkflowsForThisInstallableVersion[0] },
			});
		},
	},

	data() {
		return {
			isLoading: true,
			isInstalling: true,
			installedWorkflow: null,
			installationGoneWrong: false,
			showInstallAgainQuestion: false,
			allInstalledWorkflowsForThisInstallableVersion: [],
		};
	},
	async created() {
		await this.initUser({ avoidRedirect: true });
		AnalyticsService.sendEvent('Install initiated', {
			loggedIn: !!this.user,
			installableId: this.installableId,
			isMobile: this.isMobile,
			hasInstallPermission: !!this.user && this.canInstall,
		});

		if (!this.user) {
			const utmParams = SignUpService.getReferralAndUtmParams(this.$route.query, false);
			this.$router.push({
				name: 'InstallationSignup',
				query: {
					return_to: window.location.href,
					installableId: this.installableId,
					/* Pass along utm as well */
					...utmParams,
				},
				params: {
					installableId: this.installableId,
				},
			});
			return;
		}

		if (!this.canInstall) {
			this.isLoading = false;
			return;
		}

		await this[getInstallableAction.fullPath]({ id: this.installableId, forceGet: true });
		this.isLoading = false;

		await this.initialize();
		if (!this.didUserInstallBefore || this.forceInstall) {
			await this.createInstallable();
		}
	},
};
</script>

<style lang="scss" scoped>
.content-container {
	min-width: 820px;
	max-width: 820px;

	@media (max-height: 800px) {
		min-height: 640px;
		min-height: 640px;
	}
	@media (min-height: 800px) {
		min-height: 840px;
		min-height: 840px;
	}
	border-radius: 10px;
	box-shadow: 0px 3px 6px var(--shadow-color);
	background: var(--setup-node-container-background);
}
.loading-container {
	min-height: 650px;
	.installing-text {
		font-size: 20px;
		font-weight: 600;
		color: var(--primary-font-color);
	}
}

.installation-content-container {
	width: 520px;
	.security-info-card {
		height: 30px;
		font-size: 13px;
		border-radius: 7px;
		background: #ebedf5;
		color: var(--secondary-font-color);
		.link {
			cursor: pointer;
			text-decoration: underline;
		}
	}
	.setup-menu-container {
		@media (max-height: 800px) {
			height: 460px;
		}
		@media (min-height: 800px) {
			height: 640px;
		}
		border-radius: 10px;
		background: var(--bg-color);
		box-shadow: 0px 3px 6px var(--shadow-color);
	}
}

.no-button {
	padding: 0px;
	width: 200px;
	color: var(--primary-color);
	border: 1px solid var(--primary-color);
	background: var(--bg-color) !important;
}
.ply-details-header {
	height: 25px;
	background: var(--bg-color);
	border-radius: 10px 10px 0px 0px;

	.powered-by-text {
		font-size: 11px;
		color: #696a7a;
		text-decoration: none;
	}
}

.user-name-text {
	font-size: 11px;
	color: var(--primary-font-color);
}
.login-button {
	cursor: pointer;
	font-size: 13px;
	text-decoration: underline;
	color: var(--primary-font-color);
}

.page-container {
	width: 100vw;
	height: 100vh;
}
.install-again-text {
	cursor: pointer;
	font-size: 16px;
	text-decoration: underline;
	color: var(--primary-font-color);
}
</style>
